import React from "react";
import { useNavigate } from "react-router-dom";
import useTableSort from "../../../hooks/useTableSort";
import useModRollsData from "../../../hooks/useModRollsData";
import SortableTableHeader from "../../modalContent/SortableTableHeader";
import "../styles/modRollTimes.css";

const ModRollTime = () => {
  const navigate = useNavigate();

  // ── Live roll time data ────────────────────────────────────────────────────
  // Replaces the inline mock arrays. todaysRolls and last7BusinessDays are
  // empty arrays during loading so useTableSort receives a stable input type.
  const { todaysRolls, last7BusinessDays, loading, error } = useModRollsData();

  // ── Table sorting — today's rolls ─────────────────────────────────────────
  const {
    sortedData: sortedTodayRolls,
    requestSort: requestTodaySort,
    getSortClass: getTodaySortClass,
  } = useTableSort(todaysRolls, {
    initialKey: "datetime",
    initialDirection: "desc",
  });

  // ── Table sorting — last 7 business days ──────────────────────────────────
  const {
    sortedData: sortedLast7Days,
    requestSort: requestLast7Sort,
    getSortClass: getLast7SortClass,
  } = useTableSort(last7BusinessDays, {
    initialKey: "date",
    initialDirection: "desc",
  });

  // Navigate to the Production Milestones page and auto-filter the Weekly Mod
  // Rolls table to the business week that contains the clicked date.
  const handleRowClick = (date) => {
    navigate("/production-milestones", {
      state: { rollDate: date },
    });
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  // Format an ISO-8601 datetime string to "YYYY-MM-DD HH:MM" local display.
  // Returns "—" for null/empty values (e.g. first roll of the day has no gap).
  const fmtDatetime = (iso) => {
    if (!iso) return "—";
    // Slice to "YYYY-MM-DDTHH:MM" then replace the T separator with a space.
    return iso.slice(0, 16).replace("T", " ");
  };

  // Format a nullable number as a string, falling back to "—".
  // Used for rollTimeMinutes and avgRollTime which are legitimately null when
  // no inter-roll gap exists (first roll of day / only one roll that day).
  const fmtNullableNum = (val) => (val != null ? String(val) : "—");

  // Format the lineLeaders array for the 7-day table.
  // Joins multiple leaders with " / "; returns "—" for an empty array.
  const fmtLineLeaders = (leaders) =>
    leaders.length > 0 ? leaders.join(" / ") : "—";

  return (
    <>
      <p className="modal-description">
        Time required to move units between stations
      </p>

      {/* ── Error state ───────────────────────────────────────────────────── */}
      {error && (
        <p className="modal-error-banner">
          Failed to load roll time data: {error}
        </p>
      )}

      {/* ── Today's Line Rolls ────────────────────────────────────────────── */}
      <div className="modal-section modal-section--scroll">
        <h3>Today's Line Rolls</h3>
        <div className="modal-table-scroll">
          <table className="modal-table modal-table--clickable modal-table--mod-roll-time">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="datetime"
                  onSort={requestTodaySort}
                  sortClass={getTodaySortClass("datetime")}
                >
                  Date/Time
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="rollTimeMinutes"
                  onSort={requestTodaySort}
                  sortClass={getTodaySortClass("rollTimeMinutes")}
                >
                  Roll Time (minutes)
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="totalUnitCount"
                  onSort={requestTodaySort}
                  sortClass={getTodaySortClass("totalUnitCount")}
                >
                  Unit Count
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="lineLeader"
                  onSort={requestTodaySort}
                  sortClass={getTodaySortClass("lineLeader")}
                >
                  Line Leader
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="modal-table-loading">
                    Loading…
                  </td>
                </tr>
              ) : sortedTodayRolls.length === 0 ? (
                <tr>
                  <td colSpan={4} className="modal-table-empty">
                    No rolls recorded today.
                  </td>
                </tr>
              ) : (
                sortedTodayRolls.map((row, idx) => (
                  <tr
                    key={idx}
                    className="modal-table-row--clickable"
                    onClick={() => handleRowClick(row.datetime.slice(0, 10))}
                    title="Click for roll details"
                  >
                    <td>{fmtDatetime(row.datetime)}</td>
                    <td>{fmtNullableNum(row.rollTimeMinutes)}</td>
                    <td>{row.totalUnitCount}</td>
                    <td>{row.lineLeader ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Last 7 Business Days ──────────────────────────────────────────── */}
      <div className="modal-section modal-section--scroll">
        <h3>Last 7 Business Days</h3>
        <div className="modal-table-scroll">
          <table className="modal-table modal-table--clickable modal-table--mod-roll-time">
            <thead>
              <tr>
                <SortableTableHeader
                  columnKey="date"
                  onSort={requestLast7Sort}
                  sortClass={getLast7SortClass("date")}
                >
                  Date
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="avgRollTime"
                  onSort={requestLast7Sort}
                  sortClass={getLast7SortClass("avgRollTime")}
                >
                  Avg Roll Time (Minutes)
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="avgUnitsPerRoll"
                  onSort={requestLast7Sort}
                  sortClass={getLast7SortClass("avgUnitsPerRoll")}
                >
                  Avg Units / Roll
                </SortableTableHeader>
                <SortableTableHeader
                  columnKey="lineLeaders"
                  onSort={requestLast7Sort}
                  sortClass={getLast7SortClass("lineLeaders")}
                >
                  Line Leader(s)
                </SortableTableHeader>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="modal-table-loading">
                    Loading…
                  </td>
                </tr>
              ) : sortedLast7Days.length === 0 ? (
                <tr>
                  <td colSpan={4} className="modal-table-empty">
                    No roll data in the last 7 business days.
                  </td>
                </tr>
              ) : (
                sortedLast7Days.map((row, idx) => (
                  <tr
                    key={idx}
                    className="modal-table-row--clickable"
                    onClick={() => handleRowClick(row.date)}
                    title="Click for roll details"
                  >
                    <td>{row.date}</td>
                    <td>{fmtNullableNum(row.avgRollTime)}</td>
                    <td>{row.avgUnitsPerRoll}</td>
                    <td>{fmtLineLeaders(row.lineLeaders)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ModRollTime;
